class BeyondImageCarouselTrack extends HTMLElement {
  #moved = false;
  #startX = 0;
  #startScroll = 0;
  #pointerId = null;
  #settleRaf = 0;

  connectedCallback() {
    this.addEventListener('pointerdown', this.#onPointerDown);
    this.addEventListener('dragstart', this.#onDragStart);
    this.#pinStart();
    requestAnimationFrame(() => this.#pinStart());
  }

  disconnectedCallback() {
    this.removeEventListener('pointerdown', this.#onPointerDown);
    this.removeEventListener('dragstart', this.#onDragStart);
    this.#unbindMove();
    this.#cancelSettle();
  }

  #pinStart() {
    if (this.scrollLeft !== 0) this.scrollLeft = 0;
  }

  #cancelSettle() {
    if (this.#settleRaf) {
      cancelAnimationFrame(this.#settleRaf);
      this.#settleRaf = 0;
    }
  }

  #onDragStart = (event) => {
    event.preventDefault();
  };

  #onPointerDown = (event) => {
    if (event.pointerType === 'touch') return;
    if (event.button !== 0) return;

    this.#cancelSettle();
    this.#startX = event.clientX;
    this.#startScroll = this.scrollLeft;
    this.#moved = false;
    this.#pointerId = event.pointerId;

    // Disable soft snap for the active drag only
    this.style.scrollSnapType = 'none';
    this.setPointerCapture(event.pointerId);
    this.addEventListener('pointermove', this.#onPointerMove);
    this.addEventListener('pointerup', this.#onPointerUp);
    this.addEventListener('pointercancel', this.#onPointerUp);
  };

  #onPointerMove = (event) => {
    const delta = event.clientX - this.#startX;

    if (!this.#moved && Math.abs(delta) < 3) return;

    if (!this.#moved) {
      this.#moved = true;
      this.setAttribute('dragging', '');
    }

    this.scrollLeft = Math.max(0, this.#startScroll - delta);
  };

  #onPointerUp = () => {
    this.#unbindMove();

    if (this.#pointerId != null) {
      try {
        this.releasePointerCapture(this.#pointerId);
      } catch {
        // Pointer may already be released
      }
      this.#pointerId = null;
    }

    if (this.#moved) {
      this.addEventListener(
        'click',
        (event) => {
          event.preventDefault();
          event.stopPropagation();
        },
        { capture: true, once: true }
      );
      this.#moved = false;
      this.removeAttribute('dragging');
      this.#settleToNearest();
      return;
    }

    this.#moved = false;
    this.removeAttribute('dragging');
    this.style.scrollSnapType = '';
  };

  #nearestScrollLeft() {
    const items = this.querySelectorAll('.beyond-image-carousel__item');
    if (!items.length) return this.scrollLeft;

    const paddingStart = Number.parseFloat(getComputedStyle(this).paddingInlineStart) || 0;
    const maxScroll = Math.max(0, this.scrollWidth - this.clientWidth);
    const current = this.scrollLeft;
    let nearest = current;
    let bestDistance = Infinity;

    for (const item of items) {
      // Align item start with the content inset (same as scroll-padding / visual start)
      const target = Math.min(maxScroll, Math.max(0, item.offsetLeft - paddingStart));
      const distance = Math.abs(target - current);
      if (distance < bestDistance) {
        bestDistance = distance;
        nearest = target;
      }
    }

    return nearest;
  }

  #settleToNearest() {
    const from = this.scrollLeft;
    const to = this.#nearestScrollLeft();
    const distance = to - from;

    // Already on a snap point — just restore proximity CSS
    if (Math.abs(distance) < 1) {
      this.style.scrollSnapType = '';
      return;
    }

    const duration = 300;
    const start = performance.now();

    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      // Ease-out cubic — quick start, soft landing
      const eased = 1 - (1 - t) ** 3;
      this.scrollLeft = from + distance * eased;

      if (t < 1) {
        this.#settleRaf = requestAnimationFrame(tick);
        return;
      }

      this.#settleRaf = 0;
      this.scrollLeft = to;
      this.style.scrollSnapType = '';
    };

    this.#settleRaf = requestAnimationFrame(tick);
  }

  #unbindMove() {
    this.removeEventListener('pointermove', this.#onPointerMove);
    this.removeEventListener('pointerup', this.#onPointerUp);
    this.removeEventListener('pointercancel', this.#onPointerUp);
  }
}

if (!customElements.get('beyond-image-carousel-track')) {
  customElements.define('beyond-image-carousel-track', BeyondImageCarouselTrack);
}
